(function () {
  const ELEMENT_NAME = 'page-navigation-header';

  if (customElements.get(ELEMENT_NAME)) return;

  class PageNavigationHeader extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.sectionContainer = this.closest('.shopify-section');
      this.navigationViewport = this.querySelector('.page-navigation-header__navigation');
      this.mobileNavigationToggle = this.querySelector('.page-navigation-header__mobile-toggle');
      this.navigationLinks = Array.from(
        this.querySelectorAll('.page-navigation-header__link[data-navigation-target]')
      );
      this.stickyEnabled = this.dataset.sticky === 'true';
      this.stickyTriggerKey = (this.dataset.stickyTrigger || '').replace(/^#/, '').trim();
      this.isStickyActive = false;
      this.updateHashEnabled = this.dataset.updateHash === 'true';
      this.mobileTitleEnabled = this.dataset.mobileTitleEnabled === 'true';
      this.isMobileNavigationExpanded = false;
      this.activeKey = '';
      this.scrollFrame = null;

      this.handleLinkClick = this.handleLinkClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleMobileNavigationToggle = this.handleMobileNavigationToggle.bind(this);

      this.navigationLinks.forEach((link) => {
        link.addEventListener('click', this.handleLinkClick);
      });

      if (this.mobileNavigationToggle) {
        this.mobileNavigationToggle.addEventListener('click', this.handleMobileNavigationToggle);
      }

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:section:load', this.handleSectionLoad);

      this.observeHeaderSize();
      this.updateMobileNavigationState();
      this.refreshTargets();
      this.updateStickyOffset();
      this.updateActiveLink();
    }

    disconnectedCallback() {
      this.navigationLinks.forEach((link) => {
        link.removeEventListener('click', this.handleLinkClick);
      });

      if (this.mobileNavigationToggle) {
        this.mobileNavigationToggle.removeEventListener('click', this.handleMobileNavigationToggle);
      }

      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);

      if (this.headerResizeObserver) this.headerResizeObserver.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

      this.isInitialized = false;
    }

    observeHeaderSize() {
      this.siteHeader = document.querySelector('.shopify-section-group-header-group .header');

      if (!this.siteHeader || typeof ResizeObserver === 'undefined') return;

      this.headerResizeObserver = new ResizeObserver(() => {
        this.updateStickyOffset();
        this.updateActiveLink();
      });
      this.headerResizeObserver.observe(this.siteHeader);
    }

    refreshTargets() {
      this.targets = this.navigationLinks
        .map((link) => ({
          key: link.dataset.navigationTarget,
          link,
          target: this.findTarget(link.dataset.navigationTarget)
        }))
        .filter((item) => item.target);
      this.stickyTriggerTarget = this.findTarget(this.stickyTriggerKey);
    }

    findTarget(key) {
      if (!key) return null;

      const normalizedKey = key.replace(/^#/, '').trim();
      const pageAnchor = Array.from(document.querySelectorAll('[data-page-anchor]')).find(
        (element) => element.getAttribute('data-page-anchor') === normalizedKey
      );

      if (pageAnchor) return pageAnchor;

      const idTarget = document.getElementById(normalizedKey);
      if (idTarget) return idTarget;

      return Array.from(
        document.querySelectorAll('.section-scroll[data-scroll], .section-scroll[data-child-scroll]')
      ).find((element) => (
        element.getAttribute('data-scroll') === normalizedKey
        || element.getAttribute('data-child-scroll') === normalizedKey
      )) || null;
    }

    getHeaderHeight() {
      if (!this.siteHeader) {
        this.siteHeader = document.querySelector('.shopify-section-group-header-group .header');
      }

      if (!this.siteHeader) return 0;

      const styles = window.getComputedStyle(this.siteHeader);
      if (styles.display === 'none' || styles.visibility === 'hidden') return 0;

      return Math.max(0, Math.round(this.siteHeader.getBoundingClientRect().height));
    }

    getScrollOffset() {
      const navigationHeight = this.isStickyActive
        ? Math.round(this.getBoundingClientRect().height)
        : 0;

      return this.getHeaderHeight() + navigationHeight;
    }

    updateStickyOffset() {
      if (!this.sectionContainer) return;

      if (this.stickyTriggerKey && (!this.stickyTriggerTarget || !this.stickyTriggerTarget.isConnected)) {
        this.stickyTriggerTarget = this.findTarget(this.stickyTriggerKey);
      }

      const headerHeight = this.getHeaderHeight();
      const triggerReached = !this.stickyTriggerKey || (
        this.stickyTriggerTarget
        && this.stickyTriggerTarget.getBoundingClientRect().top <= headerHeight
      );

      this.isStickyActive = Boolean(this.stickyEnabled && triggerReached);
      const useTriggeredPosition = Boolean(this.isStickyActive && this.stickyTriggerKey);
      const useNaturalSticky = Boolean(this.isStickyActive && !this.stickyTriggerKey);

      if (useTriggeredPosition) {
        this.sectionContainer.style.setProperty(
          '--page-navigation-header-height',
          `${Math.ceil(this.getBoundingClientRect().height)}px`
        );
      } else {
        this.sectionContainer.style.removeProperty('--page-navigation-header-height');
      }

      this.sectionContainer.classList.toggle(
        'page-navigation-header-section--sticky',
        useNaturalSticky
      );
      this.sectionContainer.classList.toggle(
        'page-navigation-header-section--triggered',
        useTriggeredPosition
      );
      this.sectionContainer.style.setProperty(
        '--page-navigation-header-sticky-top',
        `${this.isStickyActive ? headerHeight : 0}px`
      );
    }

    handleLinkClick(event) {
      const link = event.currentTarget;
      const key = link.dataset.navigationTarget;
      const target = this.findTarget(key);

      if (!target) {
        event.preventDefault();
        console.warn(`[Page Navigation Header] Target anchor "${key}" was not found.`);
        return;
      }

      event.preventDefault();

      const targetTop = target.getBoundingClientRect().top
        + window.scrollY
        - this.getScrollOffset()
        - 8;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });

      this.setActiveLink(key);

      if (this.updateHashEnabled) {
        const nextUrl = `${window.location.pathname}${window.location.search}#${key}`;
        window.history.replaceState(null, '', nextUrl);
      }
    }

    handleMobileNavigationToggle() {
      if (!this.mobileTitleEnabled) return;

      this.isMobileNavigationExpanded = !this.isMobileNavigationExpanded;
      this.updateMobileNavigationState();
      this.updateStickyOffset();
      this.updateActiveLink();
    }

    updateMobileNavigationState() {
      if (!this.mobileTitleEnabled || !this.mobileNavigationToggle) return;

      this.classList.toggle('is-mobile-navigation-expanded', this.isMobileNavigationExpanded);
      this.mobileNavigationToggle.setAttribute(
        'aria-expanded',
        String(this.isMobileNavigationExpanded)
      );
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateStickyOffset();
        this.updateActiveLink();
      });
    }

    handleResize() {
      this.updateStickyOffset();
      this.updateActiveLink();
    }

    handleSectionLoad() {
      this.refreshTargets();
      this.updateStickyOffset();
      this.updateActiveLink();
    }

    updateActiveLink() {
      if (!this.targets || !this.targets.length) return;

      const markerPosition = window.scrollY + this.getScrollOffset() + 16;
      let activeTarget = this.targets[0];
      let activeTargetTop = Number.NEGATIVE_INFINITY;

      this.targets.forEach((item) => {
        const targetTop = item.target.getBoundingClientRect().top + window.scrollY;
        if (targetTop <= markerPosition && targetTop > activeTargetTop) {
          activeTarget = item;
          activeTargetTop = targetTop;
        }
      });

      this.setActiveLink(activeTarget.key);
    }

    setActiveLink(key) {
      if (!key || this.activeKey === key) return;

      this.activeKey = key;
      let activeLink = null;

      this.navigationLinks.forEach((link) => {
        const isActive = link.dataset.navigationTarget === key;
        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'location');
          activeLink = link;
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (activeLink) this.scrollLinkIntoView(activeLink);
    }

    scrollLinkIntoView(link) {
      if (!this.navigationViewport) return;

      const viewportRect = this.navigationViewport.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const isOutside = linkRect.left < viewportRect.left || linkRect.right > viewportRect.right;

      if (!isOutside) return;

      const centeredLeft = this.navigationViewport.scrollLeft
        + linkRect.left
        - viewportRect.left
        - (viewportRect.width - linkRect.width) / 2;

      this.navigationViewport.scrollTo({
        left: Math.max(0, centeredLeft),
        behavior: 'smooth'
      });
    }
  }

  customElements.define(ELEMENT_NAME, PageNavigationHeader);
})();
